/**
 * Dice formulas as Foundry writes them, rolled here: `{(2 * (2d8 + 4))[slashing],1d6[fire,persistent]}`
 * from the engine's damage builder, `1d20+5` from an inline roll, `2d20kh` for fortune. The
 * parser keeps every die's result and which flavour (damage type, persistent, precision, splash,
 * healing) each part of the total belongs to, so a damage roll can be typed part by part.
 */

export class DiceFormulaError extends Error {}

export interface DieResult {
  faces: number;
  /** Every die rolled, in order. */
  results: number[];
  /** The results that count after keep-highest / keep-lowest. */
  kept: number[];
  /** "kh", "kl" or null. */
  keep: "kh" | "kl" | null;
  /** The flavour in force where the die sits: the damage type and category when known. */
  type: string | null;
  category: DamageCategory | null;
}

export type DamageCategory = "persistent" | "precision" | "splash";

/** One typed share of an instance's total: the damage type and category the flavour tags give it. */
export interface TypedPart {
  type: string | null;
  category: DamageCategory | null;
  value: number;
}

export interface RolledInstance {
  /** The instance's formula as written. */
  formula: string;
  /** Flavour tags on the instance ("fire", "persistent", "healing", ...). */
  flavor: string[];
  type: string;
  persistent: boolean;
  /** Foundry's kinds: damage, healing, or both (a heal spell that harms undead). */
  kind: "damage" | "healing" | "both";
  materials: string[];
  /** Foundry floors an instance and never lets it go below 0. */
  total: number;
  /** The raw value before flooring. */
  raw: number;
  parts: TypedPart[];
  dice: DieResult[];
}

export interface RolledFormula {
  formula: string;
  instances: RolledInstance[];
  /** Every die, in the order the formula names them. */
  dice: DieResult[];
  /** The sum of instance totals (persistent instances included). */
  total: number;
}

/** A die the formula needs, in order: for entering physical results. */
export interface DieSlot {
  faces: number;
  count: number;
  label: string;
}

/* ------------------------------------ Tokens ------------------------------------ */

type Token =
  | { kind: "number"; value: number }
  | { kind: "dice"; count: number; faces: number; keep: "kh" | "kl" | null; keepCount: number }
  | { kind: "ident"; value: string }
  | { kind: "flavor"; tags: string[] }
  | { kind: "op"; value: string }
  | { kind: "end" };

const DICE_PATTERN = /^(\d*)d(\d+)(?:(kh|kl|k)(\d*))?/i;

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const rest = source.slice(i);
    const ch = source[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    const dice = DICE_PATTERN.exec(rest);
    if (dice) {
      const faces = Number(dice[2]);
      if (faces < 1) throw new DiceFormulaError(`a die needs at least one face in "${dice[0]}"`);
      const rawKeep = dice[3]?.toLowerCase();
      tokens.push({ kind: "dice", count: dice[1] ? Number(dice[1]) : 1, faces, keep: rawKeep === "kl" ? "kl" : rawKeep ? "kh" : null, keepCount: dice[4] ? Number(dice[4]) : 1 });
      i += dice[0].length;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      const m = /^\d*\.?\d+/.exec(rest);
      if (!m) throw new DiceFormulaError(`unexpected "${ch}" at ${i}`);
      tokens.push({ kind: "number", value: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (ch === "[") {
      const close = source.indexOf("]", i);
      if (close < 0) throw new DiceFormulaError(`unclosed "[" at ${i}`);
      tokens.push({ kind: "flavor", tags: source.slice(i + 1, close).split(",").map((t) => t.trim().toLowerCase()).filter(Boolean) });
      i = close + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][\w]*/.exec(rest)!;
      tokens.push({ kind: "ident", value: m[0] });
      i += m[0].length;
      continue;
    }
    if ("+-*/(),{}".includes(ch)) {
      tokens.push({ kind: "op", value: ch });
      i++;
      continue;
    }
    throw new DiceFormulaError(`unexpected "${ch}" at ${i}`);
  }
  tokens.push({ kind: "end" });
  return tokens;
}

/* ------------------------------------- AST -------------------------------------- */

type Node =
  | { kind: "number"; value: number }
  | { kind: "dice"; count: number; faces: number; keep: "kh" | "kl" | null; keepCount: number }
  | { kind: "binary"; op: "+" | "-" | "*" | "/"; left: Node; right: Node }
  | { kind: "negate"; operand: Node }
  | { kind: "call"; name: string; args: Node[] }
  | { kind: "flavored"; tags: string[]; operand: Node };

interface Instance {
  formula: string;
  flavor: string[];
  node: Node;
}

class Parser {
  #tokens: Token[];
  #pos = 0;
  #source: string;

  constructor(source: string) {
    this.#source = source;
    this.#tokens = tokenize(source);
  }

  #peek(): Token {
    return this.#tokens[this.#pos]!;
  }

  #next(): Token {
    return this.#tokens[this.#pos++]!;
  }

  #isOp(value: string): boolean {
    const t = this.#peek();
    return t.kind === "op" && t.value === value;
  }

  #expect(value: string): void {
    if (!this.#isOp(value)) throw new DiceFormulaError(`expected "${value}" in "${this.#source}"`);
    this.#pos++;
  }

  /** `{a,b}` or a single instance. */
  pool(): Instance[] {
    const instances: Instance[] = [];
    if (this.#isOp("{")) {
      this.#pos++;
      for (;;) {
        instances.push(this.instance());
        if (this.#isOp(",")) {
          this.#pos++;
          continue;
        }
        break;
      }
      this.#expect("}");
    } else {
      instances.push(this.instance());
    }
    if (this.#peek().kind !== "end") throw new DiceFormulaError(`unexpected token after the formula "${this.#source}"`);
    return instances;
  }

  instance(): Instance {
    const start = this.#pos;
    const node = this.expr();
    // An instance-level flavour is the one that closes the expression: `(...)[fire]` parses as a flavoured primary,
    // so lift a trailing flavour on the whole instance out as the instance's own.
    const lifted = node.kind === "flavored";
    const { inner, flavor } = lifted ? { inner: node.operand, flavor: node.tags } : { inner: node, flavor: [] as string[] };
    const formula = this.#tokens.slice(start, lifted ? this.#pos - 1 : this.#pos).map(tokenText).join(" ").replace(/\(\s/g, "(").replace(/\s\)/g, ")").replace(/\s,/g, ",");
    return { formula, flavor, node: inner };
  }

  expr(): Node {
    let left = this.term();
    while (this.#isOp("+") || this.#isOp("-")) {
      const op = (this.#next() as { value: "+" | "-" }).value;
      left = { kind: "binary", op, left, right: this.term() };
    }
    return left;
  }

  term(): Node {
    let left = this.unary();
    while (this.#isOp("*") || this.#isOp("/")) {
      const op = (this.#next() as { value: "*" | "/" }).value;
      left = { kind: "binary", op, left, right: this.unary() };
    }
    return left;
  }

  unary(): Node {
    if (this.#isOp("-")) {
      this.#pos++;
      return { kind: "negate", operand: this.unary() };
    }
    if (this.#isOp("+")) {
      this.#pos++;
      return this.unary();
    }
    return this.postfix();
  }

  postfix(): Node {
    let node = this.primary();
    while (this.#peek().kind === "flavor") {
      const tags = (this.#next() as { tags: string[] }).tags;
      node = { kind: "flavored", tags, operand: node };
    }
    return node;
  }

  primary(): Node {
    const t = this.#next();
    if (t.kind === "number") return { kind: "number", value: t.value };
    if (t.kind === "dice") return { kind: "dice", count: t.count, faces: t.faces, keep: t.keep, keepCount: t.keepCount };
    if (t.kind === "ident") {
      this.#expect("(");
      const args: Node[] = [];
      if (!this.#isOp(")")) {
        for (;;) {
          args.push(this.expr());
          if (this.#isOp(",")) {
            this.#pos++;
            continue;
          }
          break;
        }
      }
      this.#expect(")");
      return { kind: "call", name: t.value.toLowerCase(), args };
    }
    if (t.kind === "op" && t.value === "(") {
      const inner = this.expr();
      this.#expect(")");
      return inner;
    }
    throw new DiceFormulaError(`unexpected ${describeToken(t)} in "${this.#source}"`);
  }
}

function tokenText(t: Token): string {
  switch (t.kind) {
    case "number": return String(t.value);
    case "dice": return `${t.count}d${t.faces}${t.keep ? `${t.keep}${t.keepCount === 1 ? "" : t.keepCount}` : ""}`;
    case "ident": return t.value;
    case "flavor": return `[${t.tags.join(",")}]`;
    case "op": return t.value;
    case "end": return "";
  }
}

function describeToken(t: Token): string {
  return t.kind === "end" ? "end of formula" : `"${tokenText(t)}"`;
}

/* ---------------------------------- Evaluation ---------------------------------- */

const CATEGORIES: readonly DamageCategory[] = ["persistent", "precision", "splash"];
const NOT_TYPES = new Set(["persistent", "precision", "splash", "healing", "damage", "bleed-persistent"]);
/** Foundry's precious-material damage effects (system/damage/values.ts). */
const MATERIALS = new Set(["abysium", "adamantine", "cold-iron", "dawnsilver", "djezet", "duskwood", "inubrix", "keep-stone", "noqual", "orichalcum", "peachwood", "siccatite", "silver", "sisterstone", "sisterstone-dusk", "sisterstone-scarlet", "sovereign-steel", "warpglass"]);

const FUNCTIONS: Record<string, (...args: number[]) => number> = {
  abs: (a) => Math.abs(a!),
  ceil: (a) => Math.ceil(a!),
  floor: (a) => Math.floor(a!),
  round: (a) => Math.round(a!),
  max: (...args) => Math.max(...args),
  min: (...args) => Math.min(...args),
};

interface Evaluated {
  value: number;
  parts: TypedPart[];
}

interface Context {
  supply: (faces: number) => number;
  dice: DieResult[];
  type: string | null;
  category: DamageCategory | null;
}

function flavorTags(tags: string[]): { type: string | null; category: DamageCategory | null } {
  const category = tags.find((t): t is DamageCategory => (CATEGORIES as readonly string[]).includes(t)) ?? (tags.includes("bleed") ? "persistent" : null);
  const type = tags.find((t) => !NOT_TYPES.has(t) && !MATERIALS.has(t)) ?? null;
  return { type, category };
}

function evaluate(node: Node, ctx: Context): Evaluated {
  switch (node.kind) {
    case "number":
      return { value: node.value, parts: [{ type: null, category: null, value: node.value }] };
    case "dice": {
      const count = Math.max(0, Math.trunc(node.count));
      const results: number[] = [];
      for (let i = 0; i < count; i++) results.push(ctx.supply(node.faces));
      let kept = results;
      if (node.keep) {
        const sorted = [...results].sort((a, b) => (node.keep === "kh" ? b - a : a - b));
        kept = sorted.slice(0, Math.max(1, node.keepCount));
      }
      const value = kept.reduce((s, r) => s + r, 0);
      ctx.dice.push({ faces: node.faces, results, kept, keep: node.keep, type: ctx.type, category: ctx.category });
      return { value, parts: [{ type: null, category: null, value }] };
    }
    case "negate": {
      const inner = evaluate(node.operand, ctx);
      return { value: -inner.value, parts: inner.parts.map((p) => ({ ...p, value: -p.value })) };
    }
    case "binary": {
      const left = evaluate(node.left, ctx);
      const right = evaluate(node.right, ctx);
      switch (node.op) {
        case "+":
          return { value: left.value + right.value, parts: [...left.parts, ...right.parts] };
        case "-":
          return { value: left.value - right.value, parts: [...left.parts, ...right.parts.map((p) => ({ ...p, value: -p.value }))] };
        case "*": {
          // `2 * (...)` (critical doubling) scales every typed share; a product of two rolled sides scales the right by the left's value.
          const leftConstant = node.left.kind === "number" || (left.parts.length === 1 && !hasDice(node.left));
          const scaled = leftConstant ? right.parts.map((p) => ({ ...p, value: p.value * left.value })) : left.parts.map((p) => ({ ...p, value: p.value * right.value }));
          return { value: left.value * right.value, parts: scaled };
        }
        case "/": {
          const divisor = right.value === 0 ? 1 : right.value;
          return { value: left.value / divisor, parts: left.parts.map((p) => ({ ...p, value: p.value / divisor })) };
        }
      }
      break;
    }
    case "call": {
      const fn = FUNCTIONS[node.name];
      if (!fn) throw new DiceFormulaError(`unknown function "${node.name}"`);
      const args = node.args.map((a) => evaluate(a, ctx));
      const value = fn(...args.map((a) => a.value));
      // A function's result is one untyped share (the arguments' typing cannot survive min/max).
      return { value, parts: [{ type: null, category: null, value }] };
    }
    case "flavored": {
      const { type, category } = flavorTags(node.tags);
      const inner = evaluate(node.operand, { ...ctx, type: type ?? ctx.type, category: category ?? ctx.category });
      return { value: inner.value, parts: inner.parts.map((p) => ({ type: p.type ?? type, category: p.category ?? category, value: p.value })) };
    }
  }
  throw new DiceFormulaError("unreachable");
}

function hasDice(node: Node): boolean {
  switch (node.kind) {
    case "dice": return true;
    case "number": return false;
    case "negate": return hasDice(node.operand);
    case "flavored": return hasDice(node.operand);
    case "binary": return hasDice(node.left) || hasDice(node.right);
    case "call": return node.args.some(hasDice);
  }
}

function mergeParts(parts: TypedPart[]): TypedPart[] {
  const out = new Map<string, TypedPart>();
  for (const p of parts) {
    const key = `${p.type ?? ""}|${p.category ?? ""}`;
    const existing = out.get(key);
    if (existing) existing.value += p.value;
    else out.set(key, { ...p });
  }
  return [...out.values()].filter((p) => p.value !== 0);
}

function diceSlots(node: Node, label: string, out: DieSlot[]): void {
  switch (node.kind) {
    case "dice":
      out.push({ faces: node.faces, count: Math.max(0, Math.trunc(node.count)), label });
      return;
    case "number":
      return;
    case "negate":
      return diceSlots(node.operand, label, out);
    case "flavored": {
      const { type, category } = flavorTags(node.tags);
      return diceSlots(node.operand, [category, type].filter(Boolean).join(" ") || label, out);
    }
    case "binary":
      diceSlots(node.left, label, out);
      diceSlots(node.right, label, out);
      return;
    case "call":
      for (const a of node.args) diceSlots(a, label, out);
      return;
  }
}

/* ------------------------------------- API -------------------------------------- */

/** Whether the formula parses. */
export function isValidFormula(formula: string): boolean {
  try {
    new Parser(formula).pool();
    return true;
  } catch {
    return false;
  }
}

/** The dice a formula rolls, in order, each labelled by its flavour; for the physical die entry. */
export function diceOf(formula: string): DieSlot[] {
  const out: DieSlot[] = [];
  for (const instance of new Parser(formula).pool()) {
    const { type, category } = flavorTags(instance.flavor);
    diceSlots(instance.node, [category, type].filter(Boolean).join(" "), out);
  }
  return out;
}

/** How many individual dice a formula rolls. */
export function diceCount(formula: string): number {
  return diceOf(formula).reduce((n, d) => n + d.count, 0);
}

/**
 * Roll a formula with the supply. Each instance is floored at 0 as Foundry's DamageInstance is;
 * the caller applies the "at least 1" rule for damage.
 */
export function rollFormula(formula: string, supply: (faces: number) => number): RolledFormula {
  const instances = new Parser(formula).pool();
  const dice: DieResult[] = [];
  const rolled = instances.map((instance): RolledInstance => {
    const { type, category } = flavorTags(instance.flavor);
    const ctx: Context = { supply, dice: [], type, category };
    const result = evaluate(instance.node, ctx);
    dice.push(...ctx.dice);
    const persistent = category === "persistent" || instance.flavor.includes("persistent") || instance.flavor.includes("bleed");
    const resolvedType = type ?? (instance.flavor.includes("bleed") ? "bleed" : "untyped");
    const canBeHealing = !persistent && ["vitality", "void", "untyped"].includes(resolvedType);
    const kind: RolledInstance["kind"] = canBeHealing && instance.flavor.includes("healing") ? (instance.flavor.includes("damage") ? "both" : "healing") : "damage";
    const parts = mergeParts(result.parts.map((p) => ({ type: p.type ?? resolvedType, category: p.category ?? category ?? (persistent ? "persistent" : null), value: p.value })));
    return {
      formula: instance.formula,
      flavor: instance.flavor,
      type: resolvedType,
      persistent,
      kind,
      materials: instance.flavor.filter((t) => MATERIALS.has(t)),
      total: Math.max(0, Math.floor(result.value)),
      raw: result.value,
      parts,
      dice: ctx.dice,
    };
  });
  return { formula, instances: rolled, dice, total: rolled.reduce((s, i) => s + i.total, 0) };
}

/** A simple rendering: "2d8 + 4 slashing" for one instance, "1d6 fire" for the next. */
export function describeInstance(instance: Pick<RolledInstance, "formula" | "type" | "persistent" | "kind">): string {
  const type = instance.type === "untyped" ? "" : instance.type;
  return [instance.formula, instance.persistent ? "persistent" : "", type, instance.kind === "healing" ? "healing" : instance.kind === "both" ? "damage or healing" : ""].filter(Boolean).join(" ");
}
